import { getAnalytics } from "@tm/insurance/analytics";
import {
  IntersectionObserverData,
  useIntersectionObserver,
} from "@tm/ui/hooks";
import { theme } from "@tm/utility/global";
import React, { useCallback, useRef } from "react";
import FormattedText, { TextNodeType } from "../FormattedText/FormattedText";
import {
  AmountBreakdownList,
  AmountRow,
  CardImage,
  CardTitle,
  OfferCard,
  OfferCardWrapper,
  PaymentOfferBreakdownWrapper,
  SectionDescription,
  SectionTitle,
  TitleDescriptionWrapper,
} from "./PaymentOfferBreakdown.style";

export interface AmountItem {
  title: TextNodeType;
  cost: TextNodeType;
  bgColor?: string;
}

export interface PaymentOfferBreakdownData {
  title: TextNodeType;
  description: TextNodeType;
  card: {
    title: TextNodeType;
    image: string;
    amount: AmountItem[];
  };
}

interface PaymentOfferBreakdownProps {
  data: PaymentOfferBreakdownData;
}

const PaymentOfferBreakdown: React.FC<PaymentOfferBreakdownProps> = ({
  data,
}) => {
  if (!data || !data.card) return null;
  const analytics = getAnalytics();
  const hasTracked = useRef<boolean>(false);
  // Track component view when it comes into viewport
  const handleIntersection = useCallback((data: IntersectionObserverData) => {
    if (!hasTracked.current) {
      analytics?.trackCommonLandingEvents({
        actionType: "payment_offer_breakdown_seen",
      });
    }
    hasTracked.current = true;
  }, []);

  const { ref: paymentOfferBreakdownRef } = useIntersectionObserver(
    handleIntersection,
    {
      threshold: 0.5, // Trigger when 50% of component is visible
      triggerOnLeaveViewport: true,
    }
  );

  return (
    <PaymentOfferBreakdownWrapper ref={paymentOfferBreakdownRef}>
      <TitleDescriptionWrapper>
        {data.title && (
          <SectionTitle>
            <FormattedText value={data.title} />
          </SectionTitle>
        )}
        {data.description && (
          <SectionDescription>
            <FormattedText value={data.description} />
          </SectionDescription>
        )}
      </TitleDescriptionWrapper>
      <OfferCardWrapper>
        <OfferCard>
          {data.card.title && (
            <CardTitle>
              <FormattedText value={data.card.title} />
            </CardTitle>
          )}
          <AmountBreakdownList>
            {data.card.amount?.map((item, index) => (
              <AmountRow key={index} bgColor={item.bgColor}>
                <FormattedText
                  value={item.title}
                  baseStyle={{
                    fontWeight: 400,
                    fontSize: "14px",
                    color: theme.greyText,
                  }}
                />
                <FormattedText
                  value={item.cost}
                  baseStyle={{
                    fontWeight: 700,
                    fontSize: "14px",
                    color: theme.greyText,
                  }}
                />
              </AmountRow>
            ))}
          </AmountBreakdownList>
        </OfferCard>
        {data.card.image && <CardImage src={data.card.image} alt="offer" />}
      </OfferCardWrapper>
    </PaymentOfferBreakdownWrapper>
  );
};

export default PaymentOfferBreakdown;
